import { useState, ChangeEvent, SyntheticEvent } from "react";
import { useMutation } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../config";
import { setStoredAuthUser } from "../utils";
import { queryClient } from "../queryClient";
import { prefetchDefaultOpsOverview } from "../utils/opsOverview";

const signin = async (credentials: { email: string; password: string }) => {
  let res: Response;

  try {
    res = await fetch(`${API_URL}/auth/signin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-store, no-cache, max-age=0",
        Pragma: "no-cache",
      },
      body: JSON.stringify(credentials),
      credentials: "include",
      cache: "no-store",
    });
  } catch (error) {
    const offlineMessage =
      typeof navigator !== "undefined" && navigator.onLine === false
        ? "You appear to be offline. Please reconnect and try again."
        : "Unable to reach the server. Please try again in a moment.";
    throw new Error(offlineMessage);
  }

  if (!res.ok) {
    let message = "Invalid credentials";

    try {
      const payload = await res.json();
      if (payload && typeof payload.message === "string") {
        message = payload.message;
      }
    } catch (error) {
      // Ignore parsing errors and fall back to default message.
    }

    throw new Error(message);
  }

  return res.json();
};

const useSignin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const mutation = useMutation(signin, {
    onSuccess: (data) => {
      setError("");
      setStoredAuthUser(data);
      if (data?.role === "refuel_eu_reader") {
        queryClient.clear();
        navigate("/fuel/refuel-eu");
        return;
      }
      if (data?.role === "acmi_client") {
        navigate("/flights");
        return;
      }

      const journeyLog = new URLSearchParams(location.search).get("journeyLog");
      navigate(journeyLog && /^jl_[a-f0-9]{32}$/.test(journeyLog) ? `/ops/journey-logs/${journeyLog}` : "/ops");
      void prefetchDefaultOpsOverview({
        queryClient,
        operatorId: Number.parseInt(`${data?.operator_ref ?? ""}`, 10) || null,
      }).catch(() => {
        // Best-effort prefetch only.
      });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Unable to sign in. Please try again.";
      setError(message);
    },
  });

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    mutation.mutate({ email, password });
  };

  return {
    email,
    onEmailChange,
    password,
    onPasswordChange,
    handleSubmit,
    status: mutation.status,
    error,
  };
};

export default useSignin;
